import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconInfoCircle,
  IconShieldOff,
  IconShieldPlus,
} from '@tabler/icons-react';
import type { ClientAccessDto, ManagerOption } from '@expeditor/shared';
import { managerClientsApi } from '../../shared/api/manager-clients';
import { useAuthStore } from '../../shared/auth/auth.store';

interface Props {
  clientId: string;
}

/**
 * Блок «Доступ менеджеров к клиенту» — видим только ADMIN'у.
 *
 * Показывает таблицу активных и исторических доступов (грантов).
 * Кнопка «Выдать доступ» → модалка с выбором менеджера и опциональной
 * датой истечения. У активной записи — кнопка «Отозвать».
 */
export function ClientAccessCard({ clientId }: Props) {
  const user = useAuthStore((s) => s.user);
  if (user?.role !== 'ADMIN') return null;

  const [items, setItems] = useState<ClientAccessDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [opened, setOpened] = useState(false);
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [managerId, setManagerId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState(''); // 'YYYY-MM-DDTHH:mm'
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const data = await managerClientsApi.listAccess(clientId);
      setItems(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const openModal = async () => {
    setOpened(true);
    setManagerId(null);
    setExpiresAt('');
    setReason('');
    setFormError(null);
    if (managers.length === 0) {
      try {
        const m = await managerClientsApi.listManagers();
        setManagers(m);
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Не удалось загрузить менеджеров');
      }
    }
  };

  const submit = async () => {
    if (!managerId) return;
    setBusy(true);
    setFormError(null);
    try {
      await managerClientsApi.grantAccess(clientId, {
        managerId,
        expiresAt: expiresAt
          ? new Date(expiresAt).toISOString()
          : null,
        reason: reason || null,
      });
      setOpened(false);
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Не удалось выдать');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (accessId: string) => {
    if (!confirm('Отозвать доступ менеджера?')) return;
    try {
      await managerClientsApi.revokeAccess(clientId, accessId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отозвать');
    }
  };

  const fmt = (s: string) => new Date(s).toLocaleString('ru-RU');

  return (
    <Card withBorder>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={5}>Доступ менеджеров</Title>
          <Button
            size="xs"
            leftSection={<IconShieldPlus size={14} />}
            onClick={openModal}
          >
            Выдать доступ
          </Button>
        </Group>

        {error && (
          <Alert color="red" icon={<IconInfoCircle size={14} />}>
            {error}
          </Alert>
        )}

        {items.length === 0 ? (
          <Text size="sm" c="dimmed">
            Никому не выдано (кроме ответственного менеджера выше).
          </Text>
        ) : (
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Менеджер</Table.Th>
                <Table.Th>Кем выдан</Table.Th>
                <Table.Th>Срок</Table.Th>
                <Table.Th>Статус</Table.Th>
                <Table.Th>Причина</Table.Th>
                <Table.Th style={{ width: 60 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((a) => (
                <Table.Tr key={a.id} opacity={a.active ? 1 : 0.55}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {a.managerFullName ?? a.managerEmail}
                    </Text>
                    {a.managerFullName && (
                      <Text size="xs" c="dimmed">
                        {a.managerEmail}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{a.grantedByEmail}</Text>
                    <Text size="xs" c="dimmed">
                      {fmt(a.createdAt)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {a.expiresAt ? (
                      <Text size="xs">до {fmt(a.expiresAt)}</Text>
                    ) : (
                      <Text size="xs" c="dimmed">
                        бессрочно
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {a.revokedAt ? (
                      <Badge color="gray">отозван {fmt(a.revokedAt)}</Badge>
                    ) : a.active ? (
                      <Badge color="green">активен</Badge>
                    ) : (
                      <Badge color="gray">истёк</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{a.reason ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    {a.active && (
                      <Tooltip label="Отозвать">
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => revoke(a.id)}
                        >
                          <IconShieldOff size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <Modal opened={opened} onClose={() => setOpened(false)} title="Выдать доступ менеджеру" size="md">
        <Stack>
          <Select
            label="Менеджер"
            placeholder="Выберите"
            data={managers.map((m) => ({
              value: m.id,
              label: m.fullName ? `${m.fullName} (${m.email})` : m.email,
            }))}
            value={managerId}
            onChange={setManagerId}
            searchable
            required
          />
          <TextInput
            label="Срок действия (опционально)"
            description="Если оставить пустым — доступ бессрочный"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.currentTarget.value)}
          />
          <Textarea
            label="Причина (для аудита, опц.)"
            placeholder="Например: подмена основного менеджера на отпуск"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={2}
            maxRows={4}
          />
          {formError && (
            <Alert color="red" icon={<IconInfoCircle size={14} />}>
              {formError}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpened(false)}>
              Отмена
            </Button>
            <Button onClick={submit} disabled={!managerId} loading={busy}>
              Выдать
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
